import type { Side } from './types';

/**
 * The conversion-event catalog (PX-268).
 *
 * These seven events are the funnel the ad platforms optimise against.
 * They are deliberately a closed catalog rather than free-form
 * `track`/`ui` events, because unlike product analytics they have
 * consumers outside this codebase: a server-side CAPI module maps them to
 * Meta/Reddit conversions, and the warehouse reports the funnel off them.
 * A typo in a `track` event name costs a dashboard; a typo here silently
 * stops campaign optimisation.
 *
 * Three properties of the catalog carry the design decisions:
 *
 * 1. **One name per funnel step, plus a `side` property** — never
 *    `participant_signup_completed` / `researcher_signup_completed`.
 *    Platforms build side-specific conversions from the parameter, and a
 *    single name keeps the funnel comparable across both sides of the
 *    marketplace.
 * 2. **Names are domain-true.** `screener_submitted`, not
 *    `SubmitApplication`. Platform-standard names are applied at the edge
 *    (the CAPI module's mapping table) where the platform's vocabulary is
 *    already in scope. Funnel-tail events stay custom on every platform —
 *    never `Purchase`, since the money flows to the participant and
 *    `Purchase` semantics would train delivery on nonsense.
 * 3. **Only two events carry a shared id.** See
 *    `SHARED_ID_CONVERSION_EVENT_NAMES`.
 */
export const CONVERSION_EVENT_NAMES = [
  'signup_started',
  'signup_completed',
  'screener_started',
  'screener_submitted',
  'screener_qualified',
  'screener_invited',
  'screener_paid',
] as const;

export type ConversionEventName = (typeof CONVERSION_EVENT_NAMES)[number];

/**
 * The events this app emits from the browser. Each corresponds to a
 * moment the client is the first — and in some cases only — witness to.
 */
export const CLIENT_CONVERSION_EVENT_NAMES = [
  'signup_started',
  'signup_completed',
  'screener_started',
  'screener_submitted',
] as const satisfies readonly ConversionEventName[];

export type ClientConversionEventName =
  (typeof CLIENT_CONVERSION_EVENT_NAMES)[number];

/**
 * Events the browser cannot observe: they happen after the participant
 * has gone, in core-api (qualification decision, invite issued, incentive
 * paid). They are listed here anyway so the catalog is the single place
 * the funnel is written down — the edge mapping and the server-side
 * sender both read their names from here — but `AnalyticsClient.conversion`
 * will not accept them, and the type system enforces that.
 */
export const SERVER_ONLY_CONVERSION_EVENT_NAMES = [
  'screener_qualified',
  'screener_invited',
  'screener_paid',
] as const satisfies readonly ConversionEventName[];

export type ServerOnlyConversionEventName =
  (typeof SERVER_ONLY_CONVERSION_EVENT_NAMES)[number];

/**
 * The only two events sent from BOTH the browser and the server, and so
 * the only two that need a shared id.
 *
 * The ad platforms receive each of them twice — once from the browser
 * pixel, once from core-api — and collapse the two on this id (Meta
 * `event_id`, Reddit `conversion_id`). Both sides derive it rather than
 * exchange it, from the one implementation they share:
 * `signupCompletedDedupId` and `screenerSubmittedDedupId` in `@common/utils`.
 *
 * Every other event is sent from exactly one side, so an id would have
 * nothing to pair with. Giving them one anyway would invite the next
 * person to assume a dedup relationship that does not exist.
 */
export const SHARED_ID_CONVERSION_EVENT_NAMES = [
  'signup_completed',
  'screener_submitted',
] as const satisfies readonly ClientConversionEventName[];

export type SharedIdConversionEventName =
  (typeof SHARED_ID_CONVERSION_EVENT_NAMES)[number];

export type UnsharedClientConversionEventName = Exclude<
  ClientConversionEventName,
  SharedIdConversionEventName
>;

/**
 * Namespace prefix for the emitted event name, e.g. `rio.signup_completed`.
 *
 * The prefix is what makes these events addressable as a group: a consumer
 * can select the whole `rio.` namespace — the warehouse in a query, the
 * CAPI mapping in its table — without enumerating the catalog.
 */
export const CONVERSION_EVENT_PREFIX = 'rio.';

/**
 * The catalog name as it goes on the wire. Most destinations receive exactly
 * this string; GA4 cannot, and knowing which is which is the difference
 * between a configured consumer and a silent one.
 *
 * - `rio.signup_completed` — Segment, the first-party collector and PostHog
 *   pass the name through untouched. This is the spelling the warehouse
 *   stores and the CAPI mapping is keyed on.
 * - `rio_signup_completed` — GA4. The GA adapter calls gtag.js directly and
 *   `sanitizeGa4EventName` substitutes `_` for the `.` GA4 forbids, since GA4
 *   drops a non-conforming name without reporting anything. gtag queues each
 *   call as an `Arguments` object on `dataLayer`, so this app's dataLayer
 *   carries the sanitised spelling too — as a queued gtag call, not as a
 *   dataLayer event a GTM trigger can key on.
 *
 * A container trigger keys on the pair the GTM adapter pushes: the dataLayer
 * event is `rio.track` (or `rio.page`), and this name is in `rio.name` — see
 * `adapters/gtm-data-layer-adapter.ts`.
 *
 * The sanitised form is derived, never written down twice:
 * `google-analytics-adapter.test.ts` pins the exact string gtag receives for
 * every catalog entry, so the sanitiser and the catalog cannot drift apart
 * unnoticed.
 */
export function conversionEventName(name: ConversionEventName): string {
  return `${CONVERSION_EVENT_PREFIX}${name}`;
}

/**
 * The marketplace side a conversion belongs to.
 *
 * `unknown` is included because it is a real routing outcome, not a
 * defect: an anonymous visitor on a route that names neither side cannot
 * be classified. A conversion is never dropped for it — an unattributed
 * conversion is still a conversion, and swallowing it would understate
 * campaign performance. The edge treats `unknown` as unattributed.
 */
export type ConversionSide = Side;

interface ConversionEventPayloadBase {
  /**
   * Override for the side derived from the current user and route. Pass
   * it only when the call site knows something the route does not — an
   * apply flow embedded on a page that names neither side, for instance.
   */
  side?: ConversionSide;
  attributes?: Record<string, unknown>;
}

/**
 * `eventId` is required on the two shared-id events and prohibited on the
 * rest — the locked "these two and only these two" decision, expressed so
 * that violating it is a type error rather than a code-review catch.
 */
export type ConversionEventPayload =
  | (ConversionEventPayloadBase & {
      name: SharedIdConversionEventName;
      eventId: string;
    })
  | (ConversionEventPayloadBase & {
      name: UnsharedClientConversionEventName;
      eventId?: never;
    });

export function isSharedIdConversionEvent(
  name: ConversionEventName,
): name is SharedIdConversionEventName {
  const shared: readonly ConversionEventName[] =
    SHARED_ID_CONVERSION_EVENT_NAMES;
  return shared.includes(name);
}

export function isClientConversionEvent(
  name: ConversionEventName,
): name is ClientConversionEventName {
  const client: readonly ConversionEventName[] = CLIENT_CONVERSION_EVENT_NAMES;
  return client.includes(name);
}
